import { forwardRef, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ViewModelEntity } from '@view-model/domain/view-model';
import { useParams } from 'react-router';
import type { RouteParams } from '@user/pages/RouteParams';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faStar,
  faPen,
  faCaretDown,
  faCaretRight,
  faFolderOpen,
  faFolderTree,
  faUser,
  faPlus,
} from '@fortawesome/free-solid-svg-icons';
import { faCopy, faTrashAlt } from '@fortawesome/free-regular-svg-icons';
import type { Workspace } from '@workspace/domain/workspace';
import { SidebarMenuItem } from './SidebarMenuItem';
import { SidebarMenuButton } from './SidebarMenuButton';
import { useCurrentUserId } from '@framework/auth';
import { useBookmark } from '@user/pages/ViewModelPage/hooks';
import { ViewModelDeleteConfirmModal, ViewModelSettingModal } from '@view-model/ui/components/ViewModel';
import { useViewModelCopy } from '@workspace/view-model-folder/hooks';
import { MoveViewModelModal } from '@workspace/view-model-folder/ui/MoveModal/MoveViewModelModal';
import { MoveToOtherWorkspaceModal } from '@workspace/view-model-folder/ui/MoveToOtherWorkspaceModal';
import { useSidebarItemMenuPermissions } from './useSidebarItemMenuPermissions';
import { ViewModelItemTitle } from './ViewModelItemTitle';
import { ConfigureIndividualPermissions } from '@workspace/view-model-folder/ui/modals/individual-permissions/ConfigureIndividualPermissions';
import { usePrimitiveValueListener } from '@framework/repository';
import { RTDBPath } from 'balus-common';
import { useIsViewModelRestrictedByGroupLicensePermission } from '@group/License';
import { RestrictedViewModelItemTitle } from './RestrictedViewModelItemTitle';

type Modals =
  'ViewModelEdit' | 'ViewModelDelete' | 'MoveViewModel' | 'MoveToOtherWorkspace' | 'ConfigureIndividualPermissions';

const useHandleModals = () => {
  const [openModal, setOpenModal] = useState<Modals | null>(null);

  return {
    openModal,
    handleViewModelEditModalOpen: useCallback(() => {
      setOpenModal('ViewModelEdit');
    }, []),
    handleViewModelDeleteModalOpen: useCallback(() => {
      setOpenModal('ViewModelDelete');
    }, []),
    handleMoveViewModelModalOpen: useCallback(() => {
      setOpenModal('MoveViewModel');
    }, []),
    handleMoveToOtherWorkspaceModalOpen: useCallback(() => {
      setOpenModal('MoveToOtherWorkspace');
    }, []),
    handleIndividualPermissionsModalOpen: useCallback(() => {
      setOpenModal('ConfigureIndividualPermissions');
    }, []),
    handleModalClose: useCallback(() => {
      setOpenModal(null);
    }, []),
  };
};

type Props = {
  viewModel: ViewModelEntity;
  workspace: Workspace;
  searchKeyword?: string;
  isOpen?: boolean;
  toggleOpen?: () => void;
  children?: React.ReactNode;
};

export const ViewModelItem = forwardRef<HTMLDivElement, Props>(
  ({ viewModel, workspace, searchKeyword, isOpen, toggleOpen, children }, ref) => {
    const { t } = useTranslation();
    const currentUserId = useCurrentUserId();
    const { viewModelId: viewModelIdParam } = useParams<RouteParams>();
    const isSelected = useMemo(() => viewModel.id === viewModelIdParam, [viewModel.id, viewModelIdParam]);
    const { isBookmarked, toggleBookmark } = useBookmark(viewModel.id);

    const [individualPermissions] = usePrimitiveValueListener(
      RTDBPath.ViewModel.individualPermissionsPath(viewModel.id)
    );

    const {
      openModal,
      handleViewModelEditModalOpen,
      handleViewModelDeleteModalOpen,
      handleMoveViewModelModalOpen,
      handleMoveToOtherWorkspaceModalOpen,
      handleIndividualPermissionsModalOpen,
      handleModalClose,
    } = useHandleModals();
    const { isMenuButtonShowable, isEditMenuButtonShowable, isEnabledIndividualPermissions } =
      useSidebarItemMenuPermissions(workspace);

    const handleViewModelCopy = useViewModelCopy(viewModel);

    // グループライセンスによる制限チェック
    const isRestricted = useIsViewModelRestrictedByGroupLicensePermission(viewModel.createdAt);

    // 制限時のレンダリング
    if (isRestricted) {
      return (
        <div className="relative" ref={ref}>
          <div className="flex items-center">
            {/* 展開ボタンはグレー表示、クリック無効 */}
            {toggleOpen && (
              <div className="mr-1 w-4">
                <FontAwesomeIcon icon={faCaretRight} className="size-4 text-gray-400" />
              </div>
            )}
            <div className="relative grow cursor-not-allowed overflow-hidden rounded-sm">
              <RestrictedViewModelItemTitle viewModelName={viewModel.name.toString()} isLocked={viewModel.isLocked} />
            </div>
          </div>
          {/* 配下のビュー（children）は非表示 */}
        </div>
      );
    }

    return (
      <div className="relative" ref={ref}>
        <div className="flex items-center">
          {toggleOpen && (
            <button className="mr-1 w-4 focus:outline-none" onClick={toggleOpen}>
              <FontAwesomeIcon icon={isOpen ? faCaretDown : faCaretRight} className="size-4" />
            </button>
          )}
          <SidebarMenuItem
            isSelected={isSelected}
            isMenuButtonShowable={isMenuButtonShowable}
            popupMenu={
              <>
                <SidebarMenuButton
                  handleClick={toggleBookmark}
                  icon={faStar}
                  title={
                    isBookmarked ? t('viewModel.removeBookmark.label') : t('sidebar.viewModelItem.menu.addBookmark')
                  }
                  iconClassName={isBookmarked ? 'text-yellow-400' : 'text-gray-400'}
                />
                <SidebarMenuButton handleClick={handleViewModelCopy} icon={faCopy} title={t('viewModel.copy.label')} />
                {isEnabledIndividualPermissions && (
                  <SidebarMenuButton
                    handleClick={handleIndividualPermissionsModalOpen}
                    iconNode={
                      <span className="relative inline-block w-6">
                        <span className="">
                          <FontAwesomeIcon icon={faUser} />
                        </span>
                        <span className="absolute right-0 top-0 inline-block size-2 -translate-x-1 text-xs">
                          <FontAwesomeIcon icon={faPlus} />
                        </span>
                      </span>
                    }
                    title={t('sidebar.tab.shared')}
                  />
                )}
                {isEditMenuButtonShowable && (
                  <>
                    <SidebarMenuButton
                      handleClick={handleMoveViewModelModalOpen}
                      icon={faFolderOpen}
                      title={t('sidebar.viewModelItem.menu.moveViewModel')}
                    />
                    <SidebarMenuButton
                      handleClick={handleMoveToOtherWorkspaceModalOpen}
                      icon={faFolderTree}
                      title={t('workspace.moveToAnother.label')}
                    />
                    <SidebarMenuButton
                      handleClick={handleViewModelEditModalOpen}
                      icon={faPen}
                      title={t('common.action.edit')}
                    />
                    <SidebarMenuButton
                      handleClick={handleViewModelDeleteModalOpen}
                      icon={faTrashAlt}
                      title={t('common.action.delete')}
                      className="text-red-700"
                    />
                  </>
                )}
              </>
            }
          >
            <ViewModelItemTitle
              viewModelId={viewModel.id}
              isViewModelPublicReadable={viewModel.isPublicReadable(workspace.setting)}
              isIndividualPermissionConfigured={!!(isEnabledIndividualPermissions && individualPermissions)}
              isLocked={viewModel.isLocked}
              viewModelName={viewModel.name.toString()}
              searchKeyword={searchKeyword}
            />
          </SidebarMenuItem>
        </div>

        {isOpen && children && <div className="ml-5">{children}</div>}

        {openModal === 'ViewModelEdit' && (
          <ViewModelSettingModal
            isOpen={openModal === 'ViewModelEdit'}
            onClose={handleModalClose}
            workspace={workspace}
            viewModel={viewModel}
          />
        )}

        {openModal === 'ViewModelDelete' && (
          <ViewModelDeleteConfirmModal
            viewModel={viewModel}
            isOpen={openModal === 'ViewModelDelete'}
            onClose={handleModalClose}
          />
        )}

        {openModal === 'MoveViewModel' && (
          <MoveViewModelModal isOpen={openModal === 'MoveViewModel'} onClose={handleModalClose} viewModel={viewModel} />
        )}

        {currentUserId && openModal === 'MoveToOtherWorkspace' && (
          <MoveToOtherWorkspaceModal
            isOpen={openModal === 'MoveToOtherWorkspace'}
            onClose={handleModalClose}
            viewModel={viewModel}
            userId={currentUserId}
            groupId={workspace.groupId}
            workspaceId={workspace.id}
          />
        )}

        {openModal === 'ConfigureIndividualPermissions' && (
          <ConfigureIndividualPermissions
            isOpen={openModal === 'ConfigureIndividualPermissions'}
            onClose={handleModalClose}
            viewModel={viewModel}
          />
        )}
      </div>
    );
  }
);

ViewModelItem.displayName = 'ViewModelItem';
